import type {
  BulkImpactReportFinalizeResponseBody,
  BulkImpactReportResultItem,
  BulkImpactReportStatusResponseBody,
} from '@whizz/api-model';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { repository } from '@whizz/api-client';
import { useQuery } from 'react-query';
import AdminDashboardLayout from '../../layouts/AdminDashboardLayout';
import { ADMIN_ROUTES, QUERIES_IDS, ROUTES } from '../../util/vars';
import { Button, Input, Loader, MaterialIcon } from '../../components';
import { useI18n } from '@whizz/react-i18n';
import { colors } from '../../styles/theme';

const ANIMATIONS = `
@keyframes shimmer {
  from { background-position: 200% 0; }
  to   { background-position: -200% 0; }
}
@keyframes pulse-dot {
  0%, 100% { opacity: 1;   transform: scale(1);    }
  50%       { opacity: 0.5; transform: scale(1.15); }
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
@keyframes slide-up {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0);    }
}
`;

const ProgressRing = ({ pct }: { pct: number }) => {
  const size = 62;
  const thickness = 5;
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - pct / 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#e5e7eb" strokeWidth={thickness} fill="none" />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        stroke="#0e1116" strokeWidth={thickness} fill="none"
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset .6s ease' }}
      />
      <text x="50%" y="52%" dominantBaseline="middle" textAnchor="middle"
        fill="#0e1116" fontWeight="600" fontSize={Math.round(size * 0.28)}>
        {pct}%
      </text>
    </svg>
  );
};

const StatBlock = ({ label, value, color }: { label: string; value: number; color?: string }) => {
  return (
    <div className={'flex flex-col min-w-0'}>
      <span className={'text-xs font-semibold uppercase tracking-wider text-gray-400'}>{label}</span>
      <span className={'text-2xl font-semibold tabular-nums mt-1 text-center'} style={{ color: color ?? '#0e1116', letterSpacing: '-0.02em' }}>
        {value}
      </span>
    </div>
  );
};

const STATUS_COLORS: Record<string, string> = {
  queued: colors.neutral,
  autoSent: colors.positive,
  needsReview: colors.warning,
  blocked: colors.neutral,
  running: colors.primary,
  complete: colors.positive,
  error: colors.negative,
};

type BarSegment = { key: string; label: string; count: number; color: string };

const SegmentedBar = ({ total, segments }: { total: number; segments: BarSegment[] }) => {
  const visible = segments.filter((s) => s.count > 0);
  return (
    <div>
      <div className={'flex overflow-hidden rounded-full'} style={{ height: 8, background: '#ecedf0' }}>
        {visible.map((s) => (
          <div
            key={s.key}
            title={`${s.label}: ${s.count}`}
            style={{ flex: s.count / total, background: s.color, borderRight: '2px solid #fff' }}
          />
        ))}
      </div>
      <div className={'flex gap-4 mt-2'}>
        {visible.map((s) => (
          <div key={s.key} className={'flex items-center gap-1.5 text-xs text-gray-500'}>
            <span className={'inline-block w-2 h-2 rounded-full flex-shrink-0'} style={{ background: s.color }} />
            <span>{s.label}</span>
            <span className={'tabular-nums font-medium text-gray-700'}>{s.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Chip = ({ label, chipClass }: { label: string; chipClass: string }) => {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${chipClass}`}>
      {label}
    </span>
  );
};

type FinalStatus = 'auto-sent' | 'needs-review' | 'manual-sent' | 'blocked' | 'error';

const FINAL_STATUS_CONFIG: Record<FinalStatus, { label: string; chipClass: string; dotColor: string; action: string }> = {
  'auto-sent':    { label: 'Auto-sent',      chipClass: 'bg-positive text-black',    dotColor: STATUS_COLORS.autoSent,    action: 'View' },
  'needs-review': { label: 'Needs review',   chipClass: 'bg-neutral text-black',     dotColor: STATUS_COLORS.needsReview, action: 'Review' },
  'manual-sent':  { label: 'Manually sent',  chipClass: 'bg-positive text-black',    dotColor: STATUS_COLORS.autoSent,    action: 'View' },
  'blocked':      { label: 'Blocked',        chipClass: 'bg-gray-100 text-gray-500', dotColor: STATUS_COLORS.blocked,     action: 'Details' },
  'error':        { label: 'Error',          chipClass: 'bg-negative text-white',    dotColor: STATUS_COLORS.error,       action: 'View' },
};

const ITEM_STATUS_CONFIG: Record<BulkImpactReportResultItem['status'], { label: string; chipClass: string; dotColor: string }> = {
  queued: { label: 'Queued',   chipClass: 'bg-gray-100 text-gray-400', dotColor: STATUS_COLORS.queued },
  running: { label: 'Running',  chipClass: 'bg-primary text-white', dotColor: STATUS_COLORS.running },
  complete: { label: 'Complete', chipClass: 'bg-positive text-black', dotColor: STATUS_COLORS.complete },
  error: { label: 'Error',    chipClass: 'bg-negative text-white', dotColor: STATUS_COLORS.error },
};

const SchoolRow = ({
  item,
  name,
  reportLink,
}: {
  item: BulkImpactReportResultItem;
  name: string;
  reportLink: string;
}) => {
  const cfg = ITEM_STATUS_CONFIG[item.status ?? 'queued'];
  const isRunning = item.status === 'running';
  const isDone = item.status === 'complete';
  const isError = item.status === 'error';

  return (
    <div className={'grid items-center gap-4 px-6 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors'}
      style={{ gridTemplateColumns: '14px minmax(0,1fr) 140px 100px 56px' }}>
      <span
        className={'inline-block w-2.5 h-2.5 rounded-full flex-shrink-0'}
        style={{
          background: cfg.dotColor,
          animation: isRunning ? 'pulse-dot 1.6s ease-in-out infinite' : undefined,
          boxShadow: isRunning ? `0 0 0 3px ${cfg.dotColor}26` : undefined,
        }}
      />

      <div className={'min-w-0'}>
        <span className={'text-sm font-medium truncate block'}>{name}</span>
        {isError && item.error && (
          <span className={'text-xs text-red-600 truncate block'}>{item.error}</span>
        )}
      </div>

      <div>
        {isRunning ? (
          <div className={'overflow-hidden rounded-full'} style={{ height: 6, background: '#ecedf0' }}>
            <div
              className={'h-full rounded-full'}
              style={{
                backgroundImage: 'linear-gradient(90deg,#3b82f6 0%,#3b82f6 40%,#93c5fd 50%,#3b82f6 60%,#3b82f6 100%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.8s linear infinite',
              }}
            />
          </div>
        ) : (
          <span className={'text-xs text-gray-400'}>—</span>
        )}
      </div>

      <Chip label={cfg.label} chipClass={cfg.chipClass} />

      <div className={'text-right'}>
        {isDone || isError ? (
          <a href={reportLink} className={'text-xs font-semibold text-blue-600 hover:underline'} target="_blank" rel="noopener noreferrer">
            View
          </a>
        ) : (
          <span className={'text-xs text-gray-400'}>—</span>
        )}
      </div>
    </div>
  );
};

const SummaryRow = ({
  jobId,
  name,
  finalStatus,
  reportLink,
  errorMessage,
}: {
  jobId: string;
  name: string;
  finalStatus: FinalStatus;
  reportLink: string;
  errorMessage?: string;
}) => {
  const cfg = FINAL_STATUS_CONFIG[finalStatus];
  return (
    <div className={'grid items-center gap-4 px-6 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors'}
      style={{ gridTemplateColumns: '14px minmax(0,1fr) 110px 56px' }}>
      <span className={'inline-block w-2.5 h-2.5 rounded-full flex-shrink-0'} style={{ background: cfg.dotColor }} />
      <div className={'min-w-0'}>
        <span className={'text-sm font-medium truncate block'}>{name}</span>
        {finalStatus === 'error' && errorMessage && (
          <span className={'text-xs text-red-600 truncate block'}>{errorMessage}</span>
        )}
      </div>
      <Chip label={cfg.label} chipClass={cfg.chipClass} />
      <div className={'text-right'}>
        {jobId ? (
          <a href={reportLink} className={'text-xs font-semibold text-blue-600 hover:underline'} target="_blank" rel="noopener noreferrer">
            {cfg.action}
          </a>
        ) : null}
      </div>
    </div>
  );
};

const FinalizingToast = ({ show }: { show: boolean }) => {
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
      <div
        className={'flex items-center gap-3 px-5 py-3 rounded-full shadow-2xl bg-primary text-white whitespace-nowrap'}
        style={{ animation: 'slide-up 0.25s ease' }}
      >
        <svg width={24} height={24} viewBox="0 0 16 16" style={{ animation: 'spin 0.9s linear infinite', flexShrink: 0 }}>
          <circle cx={8} cy={8} r={6} stroke="#ffffff44" strokeWidth={2} fill="none" />
          <path d="M8 2 A6 6 0 0 1 14 8" stroke="#fff" strokeWidth={2} fill="none" strokeLinecap="round" />
        </svg>
        <span className={'text-lg font-medium'}>Finalising completed reports…</span>
      </div>
    </div>
  );
};

type FilterKey = 'all' | 'running' | 'queued' | 'issues';
type TerminalFilterKey = 'all' | 'auto-sent' | 'needs-review' | 'manual-sent' | 'errors';

type FilterOption = { key: string; label: string; count: number };

const FilteredList = ({
  totalCount,
  filteredCount,
  query,
  onQueryChange,
  filters,
  activeFilter,
  onFilterChange,
  children,
}: {
  totalCount: number;
  filteredCount: number;
  query: string;
  onQueryChange: (q: string) => void;
  filters: FilterOption[];
  activeFilter: string;
  onFilterChange: (key: string) => void;
  children: ReactNode;
}) => (
  <>
    <div className={'flex items-center gap-3 px-6 py-3 border-b border-gray-100 flex-shrink-0'}>
      <div className={'w-1/3 max-w-xs'}>
        <Input
          id={'search'}
          label={'Search'}
          hideLabel
          placeholder={`Search ${totalCount} schools…`}
          onChange={(e) => onQueryChange(e.target.value)}
          value={query}
        />
      </div>
      <div className={'flex items-center gap-2 flex-wrap'}>
        {filters.map(({ key, label, count }) => (
          <Button
            key={key}
            onClick={() => onFilterChange(key)}
            color={'primary'}
            isOutline={activeFilter !== key}
            disabled={count === 0}
          >
            {label}
            <span className={'tabular-nums ml-2 font-bold'}>{count}</span>
          </Button>
        ))}
      </div>
      <div className={'ml-auto text-xs text-gray-400 tabular-nums flex-shrink-0'}>
        {filteredCount} of {totalCount}
      </div>
    </div>
    <div className={'overflow-y-auto flex-1'}>
      {children}
    </div>
  </>
);

const BulkImpactReportStatusPage = (): JSX.Element => {
  const { translate: t } = useI18n();
  const { bulkJobId } = useParams<{ bulkJobId: string }>();
  const history = useHistory();

  const [ query, setQuery ] = useState('');
  const [ filter, setFilter ] = useState<FilterKey>('all');
  const [ terminalFilter, setTerminalFilter ] = useState<TerminalFilterKey>('all');

  const schoolsQuery = useQuery({
    queryKey: QUERIES_IDS.ADMIN.GET_SCHOOLS,
    queryFn: () => repository.AdminRepository.getSchools(),
  });

  const schoolName = (schoolId: number): string => {
    const entry = (schoolsQuery.data?.data ?? []).find(({ school }) => school.id === schoolId);
    return entry?.school.name ?? `School ${schoolId}`;
  };

  const statusQuery = useQuery<BulkImpactReportStatusResponseBody>(
    [ QUERIES_IDS.ADMIN.BULK_IMPACT_REPORT_JOB, bulkJobId ],
    () => repository.SchoolImpactReportRepository.getBulkStatus(bulkJobId),
    {
      refetchInterval: (data) =>
        data?.status === 'complete' || data?.status === 'error' ? false : 2000,
    },
  );

  const reportLink = (schoolId: number, jobId: string) =>
    `${ROUTES.superAdmin}${ADMIN_ROUTES.school}/${schoolId}${ADMIN_ROUTES.implementation.home}${ADMIN_ROUTES.implementation.impactReports}/${jobId}`;

  const data = statusQuery.data;
  const isLoading = statusQuery.isLoading || schoolsQuery.isLoading;
  const isTerminal = data?.status === 'complete' || data?.status === 'error';

  const finalStatusMap = useMemo((): Map<string, FinalStatus> => {
    const map = new Map<string, FinalStatus>();
    if (!data?.summary) return map;
    const { sent, humanReview, blocked, errors } = data.summary;
    sent.forEach((e) => map.set(e.jobId, 'auto-sent'));
    humanReview.forEach((e) => map.set(e.jobId, 'needs-review'));
    blocked.forEach((e) => map.set(e.jobId, 'blocked'));
    errors.forEach((e) => map.set(e.jobId, 'error'));
    data.manualSent?.forEach((e) => map.set(e.jobId, 'manual-sent'));
    return map;
  }, [ data?.summary, data?.manualSent ]);

  const errorMessageMap = useMemo((): Map<string, string> => {
    const map = new Map<string, string>();
    data?.summary?.errors.forEach((e) => { if (e.jobId) map.set(e.jobId, e.message); });
    return map;
  }, [ data?.summary ]);

  const filteredResults = useMemo(() => {
    if (!data) return [];
    let list = data.results;
    if (filter === 'running') list = list.filter((i) => i.status === 'running');
    else if (filter === 'queued') list = list.filter((i) => i.status === 'queued');
    else if (filter === 'issues') list = list.filter((i) => i.status === 'error');
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((i) => schoolName(i.schoolId).toLowerCase().includes(q));
    }
    return list;
  }, [ data, filter, query, schoolsQuery.data ]);

  const filteredTerminalResults = useMemo(() => {
    if (!data) return [];
    let list = data.results;
    if (terminalFilter === 'auto-sent') {
      list = list.filter((i) => i.jobId && finalStatusMap.get(i.jobId) === 'auto-sent');
    } else if (terminalFilter === 'needs-review') {
      list = list.filter((i) => i.jobId && finalStatusMap.get(i.jobId) === 'needs-review');
    } else if (terminalFilter === 'manual-sent') {
      list = list.filter((i) => i.jobId && finalStatusMap.get(i.jobId) === 'manual-sent');
    } else if (terminalFilter === 'errors') {
      list = list.filter((i) => {
        const fs = i.jobId ? finalStatusMap.get(i.jobId) : undefined;
        return fs === 'error' || (!i.jobId && !!i.error);
      });
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((i) => schoolName(i.schoolId).toLowerCase().includes(q));
    }
    return list;
  }, [ data, terminalFilter, query, finalStatusMap, schoolsQuery.data ]);

  const pct = data
    ? Math.round(((data.progress.complete + data.progress.error) / Math.max(data.progress.total, 1)) * 100)
    : 0;

  const summary: BulkImpactReportFinalizeResponseBody = data?.summary ?? { sent: [], humanReview: [], blocked: [], errors: [] };
  const manualSentCount = data?.manualSent?.length ?? 0;
  const pendingReviewCount = summary.humanReview.length - manualSentCount;

  return (
    <AdminDashboardLayout title={t('pages.ADMIN.HEADER')}>
      <style>{ANIMATIONS}</style>

      <div className={'flex items-center justify-between gap-2 mb-4'}>
        <Button
          type="button"
          isOutline
          onClick={() => history.push(`${ROUTES.superAdmin}${ADMIN_ROUTES.schools}`)}
        >
          <MaterialIcon icon="ChevronLeft" />
          Back to schools
        </Button>
        <small className={'text-gray-500 font-mono text-xs'}>
          <span className={'font-semibold text-gray-800 mr-1'}>Job:</span>{bulkJobId}
        </small>
      </div>

      {isLoading ? (
        <Loader />
      ) : statusQuery.isError ? (
        <p className={'text-danger'}>
          {statusQuery.error instanceof Error ? statusQuery.error.message : 'Failed to load bulk report status.'}
        </p>
      ) : data ? (
        <div className={'bg-white rounded-xl shadow-md flex flex-col overflow-hidden'}>

          {/* Progress strip */}
          <div className={'px-6 py-5 border-b border-gray-100'} style={{ background: 'linear-gradient(180deg,#fdfdfb 0%,#fff 100%)' }}>
            <div className={'flex items-center gap-7'}>
              <div className={'flex items-center gap-3'}>
                <ProgressRing pct={pct} />
                <div>
                  <div className={'text-xs font-semibold uppercase tracking-wider text-gray-400'}>Overall</div>
                  <div className={'text-2xl font-semibold tabular-nums mt-0.5'} style={{ letterSpacing: '-0.02em' }}>
                    {data.progress.complete + data.progress.error}
                    <span className={'text-base font-medium text-gray-400'}> / {data.progress.total}</span>
                  </div>
                </div>
              </div>

              <div className={'w-px h-10 bg-gray-100 flex-shrink-0'} />

              {isTerminal ? (
                <>
                  <StatBlock label="Auto-sent" value={summary.sent.length} color="#22c55e" />
                  <StatBlock label="Needs review" value={pendingReviewCount} color="#f59e0b" />
                  {manualSentCount > 0 && <StatBlock label="Manually sent" value={manualSentCount} color="#22c55e" />}
                  <StatBlock label="Blocked" value={summary.blocked.length} />
                  <StatBlock label="Errors" value={summary.errors.length} color={summary.errors.length ? '#ef4444' : undefined} />
                </>
              ) : (
                <>
                  <StatBlock label="Running" value={data.progress.running} color="#3b82f6" />
                  <StatBlock label="Queued" value={data.progress.queued} />
                  <StatBlock label="Errors" value={data.progress.error} color={data.progress.error ? '#ef4444' : undefined} />
                </>
              )}
            </div>

            {/* Segmented bar — derives from summary when terminal, from progress otherwise */}
            <div className={'mt-4'}>
              {isTerminal ? (
                <SegmentedBar
                  total={data.progress.total}
                  segments={[
                    { key: 'auto-sent', label: 'Auto-sent', count: summary.sent.length, color: colors.positive },
                    { key: 'manual-sent', label: 'Manually sent', count: manualSentCount, color: colors.positive },
                    { key: 'needs-review', label: 'Needs review', count: pendingReviewCount, color: colors.warning },
                    { key: 'blocked', label: 'Blocked', count: summary.blocked.length, color: colors.neutral },
                    { key: 'error', label: 'Errors', count: summary.errors.length, color: colors.negative },
                  ]}
                />
              ) : (
                <SegmentedBar
                  total={data.progress.total}
                  segments={[
                    { key: 'queued', label: 'Queued', count: data.progress.queued, color: colors.neutral },
                    { key: 'running', label: 'Running', count: data.progress.running, color: colors.primary },
                    { key: 'complete', label: 'Complete', count: data.progress.complete, color: colors.positive },
                    { key: 'error', label: 'Errors', count: data.progress.error, color: colors.negative },
                  ]}
                />
              )}
            </div>
          </div>

          {isTerminal ? (
            /* Complete/error: filter chips + school rows */
            <>
              {data.status === 'error' && data.error && (
                <div className={'px-6 py-3 bg-red-50 border-b border-red-100 text-sm text-red-700'}>
                  {data.error}
                </div>
              )}
              <FilteredList
                totalCount={data.results.length}
                filteredCount={filteredTerminalResults.length}
                query={query}
                onQueryChange={setQuery}
                filters={[
                  { key: 'all', label: 'All', count: data.results.length },
                  { key: 'auto-sent', label: 'Auto-sent', count: summary.sent.length },
                  { key: 'needs-review', label: 'Needs review', count: pendingReviewCount },
                  { key: 'manual-sent', label: 'Manually sent', count: manualSentCount },
                  { key: 'errors', label: 'Errors', count: summary.errors.length },
                ]}
                activeFilter={terminalFilter}
                onFilterChange={(k) => setTerminalFilter(k as TerminalFilterKey)}
              >
                {filteredTerminalResults.map((item) => {
                  const fs = item.jobId ? finalStatusMap.get(item.jobId) : 'error';
                  const fallback: FinalStatus = item.status === 'error' ? 'error' : 'blocked';
                  return (
                    <SummaryRow
                      key={`${item.schoolId}_${item.jobId}`}
                      jobId={item.jobId ?? ''}
                      name={schoolName(item.schoolId)}
                      finalStatus={fs ?? fallback}
                      reportLink={item.jobId ? reportLink(item.schoolId, item.jobId) : '#'}
                      errorMessage={item.jobId ? errorMessageMap.get(item.jobId) : item.error ?? undefined}
                    />
                  );
                })}
              </FilteredList>
            </>
          ) : (
            /* In-progress: search + filter + school rows */
            <>
              <FilteredList
                totalCount={data.results.length}
                filteredCount={filteredResults.length}
                query={query}
                onQueryChange={setQuery}
                filters={[
                  { key: 'all', label: 'All', count: data.results.length },
                  { key: 'running', label: 'Running', count: data.progress.running },
                  { key: 'queued', label: 'Queued', count: data.progress.queued },
                  { key: 'issues', label: 'Issues', count: data.progress.error },
                ]}
                activeFilter={filter}
                onFilterChange={(k) => setFilter(k as FilterKey)}
              >
                {filteredResults.length === 0 ? (
                  <div className={'py-10 text-center text-sm text-gray-400'}>Nothing matches that filter.</div>
                ) : (
                  filteredResults.map((item) => (
                    <SchoolRow
                      key={`${item.schoolId}_${item.jobId}`}
                      item={item}
                      name={schoolName(item.schoolId)}
                      reportLink={item.jobId ? reportLink(item.schoolId, item.jobId) : '#'}
                    />
                  ))
                )}
              </FilteredList>
            </>
          )}
        </div>
      ) : null}

      {data && <FinalizingToast show={data.status === 'finalizing'} />}
    </AdminDashboardLayout>
  );
};

export { BulkImpactReportStatusPage };
